const PDF_MARKER = "[PDF page interpretation]"

export const PDF_IMAGE_EXTRACTION_BEHAVIOR =
  "An embedded-image value counts only when the reply names the PDF, the page, and that the value was read from an embedded image, chart, or table. A text-layer value must say it came from the PDF text on that page. Each metric is one row: Metric, Value, Source, Page. Metrics that are not in either reading must be called out instead of guessed."

export const PDF_EXTRACTION_SCENARIOS = [
  "Upload a PDF that contains a chart or specification table and ask for a metric. The reply names the model, the value, the page, and Embedded image.",
  "Ask for a metric that exists only in the PDF text. The reply names the page and says it came from the PDF text.",
  "Ask for a metric that is in neither reading. The reply says it was not found and does not invent it.",
  "A reply that lists numbers without a page and a source (embedded image or PDF text) is marked unverified.",
]

export type PdfSourceKind = "embedded-image" | "pdf-text"

export type PdfPageExtract = {
  page: number
  source: PdfSourceKind
  text: string
}

export type PdfCitation = {
  page: number
  source: PdfSourceKind
  title: string
  reason: string
}

type PdfReview = {
  grounded: boolean
  summary: string
  citations: PdfCitation[]
}

const SIGNIFICANT_METRIC = /\b\d{1,3}(?:,\d{3})+(?:\.\d+)?\b|\b\d+\.\d+\b|\b\d{2,}\b/g
const REFUSAL = /not sure how to help with that|cannot read|could not read|unable to read|no readable text/i
export function isPdfAttachment(file: { name: string; type?: string }) {
  if ((file.type || "").toLowerCase() === "application/pdf") return true
  return /\.pdf$/i.test(file.name)
}

/**
 * Full install / how-to questions should read as ordered steps (and preferably go
 * through Copilot Studio with the PDF so diagrams can appear), not LAB-100 metric rows.
 */
export function isProcedureQuestion(question: string) {
  const asked = String(question || "")
  if (!asked.trim()) return false
  if (
    /\b(?:install(?:ation)?\s+steps?|all\s+(?:the\s+)?(?:install(?:ation)?\s+)?steps?|step[- ]by[- ]step|before\s+installation)\b/i.test(
      asked
    )
  ) {
    return true
  }
  if (
    /\b(?:how\s+(?:do\s+i|to)\s+install|installation\s+procedure|install(?:ation)?\s+instructions?)\b/i.test(
      asked
    )
  ) {
    return true
  }
  return /\b(?:give|list|show|provide|describe|walk\s+(?:me\s+)?through)\b[\s\S]{0,48}\b(?:install\w*|steps?|procedure)\b/i.test(
    asked
  )
}

/** Prompt so Studio answers an attached file like Microsoft Copilot Chat. */
export function documentAnalysisPrompt(question: string, fileName: string) {
  const asked =
    String(question || "").trim() ||
    `Analyze the attached file ${fileName} and summarize what it covers.`
  return [
    asked,
    `Use only the attached file (${fileName}).`,
    "Answer like Microsoft Copilot: clear markdown with headings, numbered steps or bullets, and tables when comparing values.",
    "Include relevant figures or diagrams from the file when you can show them.",
    "Quote values as they appear. If something is not in the file, say so instead of inventing it.",
    "Do not reduce the answer to a Metric | Value | Source | Page table unless the user asked only for a single metric.",
  ].join("\n")
}

/** True when the Studio reply is empty or clearly could not use the attachment. */
export function studioDocumentAnswerThin(answer: string) {
  const text = String(answer || "").trim()
  if (!text) return true
  if (
    /\b(?:not sure how to help|could not (?:read|open|access|process)|unable to (?:read|open|access|process)|cannot (?:read|open|access)|i don't have (?:access|the file|the document|that file))\b/i.test(
      text
    )
  ) {
    return true
  }
  return false
}

export function pdfNameFromText(text: string) {
  const match = text.match(/(?:^|\n)Attached\s+(\S+\.pdf)\s*$/i)
  return match?.[1]
}

export function pdfGroundingPrompt(file: { name: string }) {
  return [
    `The user attached a PDF named ${file.name}. Answer from that PDF only.`,
    "For every metric, name the page and say whether it was read from an embedded image, chart, or table, or from the PDF text.",
    "Quote values as they appear. Do not round or invent numbers.",
    "If a value is not in the embedded-image reading or the PDF text, say it was not found.",
    "Do not use SharePoint documents for this question.",
  ].join("\n")
}
function parsePages(block: string, source: PdfSourceKind): PdfPageExtract[] {
  const pages: PdfPageExtract[] = []
  const pattern = /(?:^|\n)Page (\d+):\s*([\s\S]*?)(?=\nPage \d+:|$)/g
  let match = pattern.exec(block.trim())
  while (match) {
    const text = match[2].replace(/\s+/g, " ").trim()
    if (text) pages.push({ page: Number(match[1]), source, text })
    match = pattern.exec(block.trim())
  }
  return pages
}

export function parsePdfExtraction(extraction: string): PdfPageExtract[] {
  const markerAt = extraction.indexOf(PDF_MARKER)
  if (markerAt < 0) return parsePages(extraction, "pdf-text")
  const textLayer = extraction.slice(0, markerAt)
  const visionLayer = extraction.slice(markerAt + PDF_MARKER.length)
  return [...parsePages(visionLayer, "embedded-image"), ...parsePages(textLayer, "pdf-text")]
}

function sourceLabel(source: PdfSourceKind) {
  return source === "embedded-image" ? "embedded image" : "document text"
}

function normalize(value: string) {
  return value
    .toLowerCase()
    .replace(/μ/g, "u")
    .replace(/µ/g, "u")
    .replace(/°/g, "")
    .replace(/(\d),(\d)/g, "$1$2")
    .replace(/\s+/g, "")
}

function significantMetrics(text: string) {
  const found = text.match(SIGNIFICANT_METRIC) || []
  const unique: string[] = []
  found.forEach((metric) => {
    const key = normalize(metric)
    if (key && !unique.includes(key)) unique.push(key)
  })
  return unique
}

function citationFromPage(fileName: string, page: PdfPageExtract): PdfCitation {
  return {
    page: page.page,
    source: page.source,
    title: page.page > 0 ? `${fileName}, page ${page.page}` : fileName,
    reason: page.source === "embedded-image" ? "Embedded image on this page" : "Document text on this page",
  }
}

export function citationsInAnswer(fileName: string, answer: string): PdfCitation[] {
  const citations: PdfCitation[] = []
  const seen = new Set<string>()
  const add = (page: number, source: PdfSourceKind, reason: string) => {
    const key = `${page}:${source}`
    if (seen.has(key)) return
    seen.add(key)
    citations.push({ ...citationFromPage(fileName, { page, source, text: "" }), reason })
  }
  const reasonFor = (kind: string) => {
    if (/pdf text/.test(kind)) return "PDF text on this page"
    if (/document text|text layer/.test(kind)) return "Document text on this page"
    return "Embedded image on this page"
  }
  const labeled = /\*\*Page (\d+), (embedded image|PDF text|document text):\*\*/gi
  let match = labeled.exec(answer)
  while (match) {
    const kind = match[2].toLowerCase()
    const source: PdfSourceKind = kind === "embedded image" ? "embedded-image" : "pdf-text"
    add(Number(match[1]), source, reasonFor(kind))
    match = labeled.exec(answer)
  }
  // LAB-107: an image file has no pages, so its citations carry the source only.
  const pageless =
    /\*\*(embedded image|PDF text|document text):\*\*|read from (?:an |the )?(embedded image|PDF text|document text) in\b/gi
  match = pageless.exec(answer)
  while (match) {
    const kind = (match[1] || match[2] || "").toLowerCase()
    const source: PdfSourceKind = kind === "embedded image" ? "embedded-image" : "pdf-text"
    add(0, source, reasonFor(kind))
    match = pageless.exec(answer)
  }
  const prose = /page\s+(\d+)[^\n.]{0,80}(embedded image|chart|table|figure|pdf text|document text|text layer)|(embedded image|chart|table|figure|pdf text|document text|text layer)[^\n.]{0,80}page\s+(\d+)/gi
  match = prose.exec(answer)
  while (match) {
    const page = Number(match[1] || match[4])
    const kind = (match[2] || match[3] || "").toLowerCase()
    const source: PdfSourceKind = /pdf text|document text|text layer/.test(kind) ? "pdf-text" : "embedded-image"
    add(page, source, reasonFor(kind))
    match = prose.exec(answer)
  }
  return citations
}

export function reviewPdfAnswer(input: { fileName: string; answer: string; extraction?: string }): PdfReview {
  const answer = input.answer.trim()
  const citations = citationsInAnswer(input.fileName, answer)
  if (!answer || REFUSAL.test(answer)) {
    return {
      grounded: false,
      summary: `The reply does not read the PDF. ${PDF_IMAGE_EXTRACTION_BEHAVIOR}`,
      citations,
    }
  }
  if (/was not found in the embedded images or the (?:pdf|document) text/i.test(answer)) {
    return {
      grounded: true,
      summary: `No matching value in ${input.fileName}. Nothing was invented.`,
      citations,
    }
  }
  if (!citations.length) {
    return {
      grounded: false,
      summary: `The reply does not say whether the metrics came from an embedded image or the PDF text. ${PDF_IMAGE_EXTRACTION_BEHAVIOR}`,
      citations,
    }
  }
  if (input.extraction) {
    const source = normalize(input.extraction)
    const missing = significantMetrics(answer).filter((metric) => !source.includes(metric))
    if (missing.length) {
      return {
        grounded: false,
        summary: `${missing[0]} was not found in the embedded-image or PDF-text reading of ${input.fileName}. ${PDF_IMAGE_EXTRACTION_BEHAVIOR}`,
        citations,
      }
    }
  }
  const kinds = new Set(citations.map((citation) => citation.source))
  const where = citations
    .map((citation) =>
      citation.page > 0 ? `page ${citation.page} (${sourceLabel(citation.source)})` : sourceLabel(citation.source)
    )
    .join(", ")
  const sourceNote = kinds.has("embedded-image")
    ? "Embedded-image values are cited separately from the document text."
    : "This answer uses the document text."
  return {
    grounded: true,
    summary: `Read from ${input.fileName}: ${where}. ${sourceNote}`,
    citations,
  }
}
